import React, { useState, useEffect, useCallback } from "react";
import Icon from "components/AppIcon";
import api from "../../../lib/api";
import { useAuth } from "../../../contexts/AuthContext";

const DURATION_UNITS = ["days", "months", "years"];

const emptyForm = {
  name: "",
  duration_value: "",
  duration_unit: "months",
  price: "",
  description: "",
};

function Toast({ message, type, onClose }) {
  useEffect(() => {
    const t = setTimeout(onClose, 3500);
    return () => clearTimeout(t);
  }, [onClose]);

  const colors = {
    success: "bg-green-50 border-green-200 text-green-800",
    error: "bg-red-50 border-red-200 text-red-800",
    info: "bg-blue-50 border-blue-200 text-blue-800",
  };

  return (
    <div
      className={`fixed bottom-5 right-5 z-50 flex items-center gap-2 px-4 py-3 rounded-lg border shadow-lg text-sm font-medium ${colors?.[type] || colors?.info}`}
    >
      <Icon
        name={type === "success" ? "CheckCircle" : type === "error" ? "XCircle" : "Info"}
        size={16}
      />
      <span>{message}</span>
      <button onClick={onClose} className="ml-2 opacity-60 hover:opacity-100">
        <Icon name="X" size={14} />
      </button>
    </div>
  );
}

function ConfirmModal({ open, onConfirm, onCancel }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-[var(--color-card)] rounded-xl border border-[var(--color-border)] shadow-xl p-6 w-full max-w-sm mx-4">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-9 h-9 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
            <Icon name="Trash2" size={18} color="#ef4444" />
          </div>
          <h3 className="text-base font-bold text-[var(--color-foreground)]">Delete Package?</h3>
        </div>
        <p className="text-sm text-[var(--color-muted-foreground)] mb-5">
          This cannot be undone. The package will be permanently removed.
        </p>
        <div className="flex gap-2 justify-end">
          <button
            onClick={onCancel}
            className="px-4 py-2 text-sm rounded-md border border-[var(--color-border)] text-[var(--color-foreground)] hover:bg-[var(--color-muted)] transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            className="px-4 py-2 text-sm rounded-md bg-red-500 text-white hover:bg-red-600 transition-colors font-medium"
          >
            Delete
          </button>
        </div>
      </div>
    </div>
  );
}

function PackageFormModal({ open, onClose, onSaved, editPackage }) {
  useAuth();
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (editPackage) {
      setForm({
        name: editPackage?.name || "",
        duration_value: String(editPackage?.duration_value || ""),
        duration_unit: editPackage?.duration_unit || "months",
        price: String(editPackage?.price || ""),
        description: editPackage?.description || "",
      });
    } else {
      setForm(emptyForm);
    }
    setErrors({});
  }, [editPackage, open]);

  const validate = () => {
    const e = {};
    if (!form?.name?.trim()) e.name = "Package name is required";
    if (!form?.duration_value || isNaN(Number(form?.duration_value)) || Number(form?.duration_value) <= 0)
      e.duration_value = "Enter a valid duration";
    if (form?.price === "" || isNaN(Number(form?.price)) || Number(form?.price) < 0)
      e.price = "Enter a valid price";
    return e;
  };

  const handleSave = async () => {
    const e = validate();
    if (Object.keys(e)?.length > 0) { setErrors(e); return; }
    setSaving(true);
    try {
      const payload = {
        name: form?.name?.trim(),
        duration_value: parseInt(form?.duration_value, 10),
        duration_unit: form?.duration_unit,
        price: parseFloat(form?.price),
        description: form?.description?.trim() || null,
      };

      if (editPackage) {
        await api.put(`/packages/${editPackage?.id}`, payload);
        onSaved("Package updated successfully", "success");
      } else {
        await api.post('/packages', payload);
        onSaved("Package added successfully", "success");
      }
      onClose();
    } catch (err) {
      onSaved(err?.message || "Failed to save package", "error");
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-[var(--color-card)] rounded-xl border border-[var(--color-border)] shadow-xl p-6 w-full max-w-md mx-4">
        <div className="flex items-center justify-between mb-5">
          <h3 className="text-base font-bold text-[var(--color-foreground)]">
            {editPackage ? "Edit Package" : "Add New Package"}
          </h3>
          <button onClick={onClose} className="text-[var(--color-muted-foreground)] hover:text-[var(--color-foreground)]">
            <Icon name="X" size={18} />
          </button>
        </div>

        <div className="space-y-4">
          {/* Package Name */}
          <div>
            <label className="block text-xs font-medium text-[var(--color-foreground)] mb-1">
              Package Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={form?.name}
              onChange={e => setForm(f => ({ ...f, name: e?.target?.value }))}
              placeholder="e.g. Premium Monthly"
              className="w-full px-3 py-2 text-sm border border-[var(--color-border)] rounded-md bg-[var(--color-input)] text-[var(--color-foreground)] focus:outline-none focus:ring-2 focus:ring-[var(--color-ring)]"
            />
            {errors?.name && <p className="text-xs text-red-500 mt-1">{errors?.name}</p>}
          </div>

          {/* Duration */}
          <div>
            <label className="block text-xs font-medium text-[var(--color-foreground)] mb-1">
              Duration <span className="text-red-500">*</span>
            </label>
            <div className="flex gap-2">
              <input
                type="number"
                min="1"
                value={form?.duration_value}
                onChange={e => setForm(f => ({ ...f, duration_value: e?.target?.value }))}
                placeholder="1"
                className="w-24 px-3 py-2 text-sm border border-[var(--color-border)] rounded-md bg-[var(--color-input)] text-[var(--color-foreground)] focus:outline-none focus:ring-2 focus:ring-[var(--color-ring)]"
              />
              <select
                value={form?.duration_unit}
                onChange={e => setForm(f => ({ ...f, duration_unit: e?.target?.value }))}
                className="flex-1 px-3 py-2 text-sm border border-[var(--color-border)] rounded-md bg-[var(--color-input)] text-[var(--color-foreground)] focus:outline-none focus:ring-2 focus:ring-[var(--color-ring)]"
              >
                {DURATION_UNITS?.map(u => (
                  <option key={u} value={u}>{u?.charAt(0)?.toUpperCase() + u?.slice(1)}</option>
                ))}
              </select>
            </div>
            {errors?.duration_value && <p className="text-xs text-red-500 mt-1">{errors?.duration_value}</p>}
          </div>

          {/* Price */}
          <div>
            <label className="block text-xs font-medium text-[var(--color-foreground)] mb-1">
              Price ($) <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-[var(--color-muted-foreground)]">$</span>
              <input
                type="number"
                min="0"
                step="0.01"
                value={form?.price}
                onChange={e => setForm(f => ({ ...f, price: e?.target?.value }))}
                placeholder="0.00"
                className="w-full pl-7 pr-3 py-2 text-sm border border-[var(--color-border)] rounded-md bg-[var(--color-input)] text-[var(--color-foreground)] focus:outline-none focus:ring-2 focus:ring-[var(--color-ring)]"
              />
            </div>
            {errors?.price && <p className="text-xs text-red-500 mt-1">{errors?.price}</p>}
          </div>

          {/* Description */}
          <div>
            <label className="block text-xs font-medium text-[var(--color-foreground)] mb-1">Description</label>
            <textarea
              value={form?.description}
              onChange={e => setForm(f => ({ ...f, description: e?.target?.value }))}
              placeholder="Optional description..."
              rows={3}
              className="w-full px-3 py-2 text-sm border border-[var(--color-border)] rounded-md bg-[var(--color-input)] text-[var(--color-foreground)] focus:outline-none focus:ring-2 focus:ring-[var(--color-ring)] resize-none"
            />
          </div>
        </div>

        <div className="flex gap-2 justify-end mt-5">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm rounded-md border border-[var(--color-border)] text-[var(--color-foreground)] hover:bg-[var(--color-muted)] transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex items-center gap-1.5 px-4 py-2 text-sm rounded-md bg-[var(--color-primary)] text-white hover:bg-[var(--color-secondary)] transition-colors font-medium disabled:opacity-60"
          >
            {saving && <Icon name="Loader2" size={14} className="animate-spin" />}
            {saving ? "Saving..." : editPackage ? "Update Package" : "Add Package"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function PackagesManager() {
  useAuth();
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [editPackage, setEditPackage] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const showToast = useCallback((message, type = "info") => {
    setToast({ message, type });
  }, []);

  const fetchPackages = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/packages');
      setPackages((data || [])?.map(pkg => ({ ...pkg, memberCount: 0 })));
    } catch (err) {
      showToast(err?.message || "Failed to load packages", "error");
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => {
    fetchPackages();
  }, [fetchPackages]);

  const handleOpenAdd = () => {
    setEditPackage(null);
    setModalOpen(true);
  };

  const handleOpenEdit = (pkg) => {
    setEditPackage(pkg);
    setModalOpen(true);
  };

  const handleSaved = (message, type) => {
    showToast(message, type);
    fetchPackages();
  };

  const handleDeleteClick = (pkg) => {
    setConfirmDelete(pkg);
  };

  const handleConfirmDelete = async () => {
    if (!confirmDelete) return;
    setDeleting(true);
    try {
      await api.delete(`/packages/${confirmDelete?.id}`);
      showToast("Package deleted", "success");
      fetchPackages();
    } catch (err) {
      showToast(err?.message || "Failed to delete package", "error");
    } finally {
      setDeleting(false);
      setConfirmDelete(null);
    }
  };

  const formatDuration = (pkg) => {
    const val = pkg?.duration_value;
    const unit = pkg?.duration_unit || "months";
    return `${val} ${val === 1 ? unit?.replace(/s$/, "") : unit}`;
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-base font-bold text-[var(--color-foreground)] font-[var(--font-heading)]">
            Packages
          </h2>
          <p className="text-xs text-[var(--color-muted-foreground)] font-[var(--font-caption)]">
            Manage membership packages for your gym
          </p>
        </div>
        <button
          onClick={handleOpenAdd}
          className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium bg-[var(--color-primary)] text-white rounded-md hover:bg-[var(--color-secondary)] transition-colors"
        >
          <Icon name="Plus" size={14} color="white" />
          Add Package
        </button>
      </div>
      {/* Table */}
      <div className="bg-[var(--color-card)] rounded-xl border border-[var(--color-border)] overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Icon name="Loader2" size={24} color="var(--color-muted-foreground)" className="animate-spin" />
            <span className="ml-2 text-sm text-[var(--color-muted-foreground)]">Loading packages...</span>
          </div>
        ) : packages?.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <div className="w-12 h-12 rounded-full bg-[var(--color-muted)] flex items-center justify-center mb-3">
              <Icon name="Package" size={22} color="var(--color-muted-foreground)" />
            </div>
            <p className="text-sm font-medium text-[var(--color-foreground)]">No packages yet</p>
            <p className="text-xs text-[var(--color-muted-foreground)] mt-1">Add your first membership package to get started</p>
            <button
              onClick={handleOpenAdd}
              className="mt-4 flex items-center gap-1.5 px-3 py-2 text-sm font-medium bg-[var(--color-primary)] text-white rounded-md hover:bg-[var(--color-secondary)] transition-colors"
            >
              <Icon name="Plus" size={14} color="white" />
              Add Package
            </button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[var(--color-border)] bg-[var(--color-muted)]/40">
                  <th className="text-left px-4 py-3 text-xs font-semibold text-[var(--color-muted-foreground)] uppercase tracking-wide">Package</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-[var(--color-muted-foreground)] uppercase tracking-wide">Duration</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-[var(--color-muted-foreground)] uppercase tracking-wide">Price</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-[var(--color-muted-foreground)] uppercase tracking-wide hidden md:table-cell">Description</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-[var(--color-muted-foreground)] uppercase tracking-wide">Members</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-[var(--color-muted-foreground)] uppercase tracking-wide">Status</th>
                  <th className="text-right px-4 py-3 text-xs font-semibold text-[var(--color-muted-foreground)] uppercase tracking-wide">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[var(--color-border)]">
                {packages?.map((pkg) => {
                  const inUse = (pkg?.memberCount || 0) > 0;
                  return (
                    <tr key={pkg?.id} className="hover:bg-[var(--color-muted)]/20 transition-colors">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <div className="w-7 h-7 rounded-md bg-[var(--color-primary)]/10 flex items-center justify-center flex-shrink-0">
                            <Icon name="Package" size={13} color="var(--color-primary)" />
                          </div>
                          <span className="font-medium text-[var(--color-foreground)]">{pkg?.name}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-[var(--color-muted-foreground)]">
                        {formatDuration(pkg)}
                      </td>
                      <td className="px-4 py-3 font-semibold text-[var(--color-foreground)]">
                        ${parseFloat(pkg?.price || 0)?.toFixed(2)}
                      </td>
                      <td className="px-4 py-3 text-[var(--color-muted-foreground)] hidden md:table-cell max-w-[200px]">
                        <span className="truncate block">{pkg?.description || "—"}</span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-[var(--color-foreground)] font-medium">{pkg?.memberCount || 0}</span>
                        <span className="text-[var(--color-muted-foreground)] text-xs ml-1">active</span>
                      </td>
                      <td className="px-4 py-3">
                        {inUse ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
                            <span className="w-1.5 h-1.5 rounded-full bg-green-500 inline-block"></span>
                            In Use
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-[var(--color-muted)] text-[var(--color-muted-foreground)]">
                            <span className="w-1.5 h-1.5 rounded-full bg-gray-400 inline-block"></span>
                            Available
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1 justify-end">
                          {/* Edit button - always visible */}
                          <button
                            onClick={() => handleOpenEdit(pkg)}
                            title="Edit package"
                            className="p-1.5 rounded-md text-[var(--color-muted-foreground)] hover:text-[var(--color-primary)] hover:bg-[var(--color-primary)]/10 transition-colors"
                          >
                            <Icon name="Pencil" size={14} />
                          </button>

                          {/* Delete button - only if not in use */}
                          {inUse ? (
                            <div className="relative group">
                              <button
                                disabled
                                className="p-1.5 rounded-md text-[var(--color-border)] cursor-not-allowed"
                                title="Cannot delete — package has active members"
                              >
                                <Icon name="Trash2" size={14} />
                              </button>
                              <div className="absolute right-0 bottom-full mb-1 hidden group-hover:block z-10 w-52 px-2 py-1.5 text-xs text-white bg-gray-800 rounded-md shadow-lg whitespace-normal">
                                Cannot delete — package has active members
                              </div>
                            </div>
                          ) : (
                            <button
                              onClick={() => handleDeleteClick(pkg)}
                              title="Delete package"
                              className="p-1.5 rounded-md text-[var(--color-muted-foreground)] hover:text-red-500 hover:bg-red-50 transition-colors"
                            >
                              <Icon name="Trash2" size={14} />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
      {/* Modals */}
      <PackageFormModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onSaved={handleSaved}
        editPackage={editPackage}
      />
      <ConfirmModal
        open={!!confirmDelete}
        onConfirm={handleConfirmDelete}
        onCancel={() => setConfirmDelete(null)}
      />
      {/* Toast */}
      {toast && (
        <Toast
          message={toast?.message}
          type={toast?.type}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  );
}
